class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0, event);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    // Deferred, because QuantityInput's click handler can still change the value after this event.
    this.addEventListener('change', (event) => {
      if (!event.target.matches('.quantity__input')) return;
      setTimeout(() => this.onChange(event));
    });
  }

  cartUpdateUnsubscriber = undefined;

  // Quantities show at once and go to Shopify in the background, because a cart change takes over a
  // second on this shop. Rows changed while a request runs wait here and go out together afterwards.
  pendingRows = new Map();
  flushTimer = null;
  requestRunning = false;
  lastChange = null;
  flushOnPageHide = () => this.flushPending();

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items' || this.requestRunning || this.pendingRows.size) {
        return;
      }
      return this.onCartUpdate();
    });
    window.addEventListener('pagehide', this.flushOnPageHide);
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
    window.removeEventListener('pagehide', this.flushOnPageHide);
  }

  resetQuantityInput(input) {
    input.value = input.closest('[data-cart-item-key]').dataset.quantity;
    this.isEnterPressed = false;
  }

  setValidity(event, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(event.target);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (Number.isNaN(inputValue)) {
      this.resetQuantityInput(event.target);
      return;
    }

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        event,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      return fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      return fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  // Lines added together from one product form (e.g. shafts + shaft cutting) share a _ID property,
  // rendered as data-connection, so they change and disappear together.
  getConnectedRows(row) {
    const connection = row.querySelector('.quantity__input').dataset.connection;
    if (!connection) return [row];

    return [...this.querySelectorAll(`.quantity__input[data-connection="${connection}"]`)].map((input) =>
      input.closest('[data-cart-item-key]')
    );
  }

  updateQuantity(line, quantity, event, name, variantId) {
    const row = this.querySelector(`#CartItem-${line}, #CartDrawer-Item-${line}`);
    this.lastChange = {
      line,
      name,
      variantId,
      event,
      eventTarget: event.currentTarget instanceof CartRemoveButton ? 'clear' : 'change',
    };

    this.getConnectedRows(row).forEach((connectedRow) => {
      this.showQuantity(connectedRow, quantity);
      this.pendingRows.set(connectedRow, quantity);
    });
    this.showTotals();
    this.setUpdating(true);

    clearTimeout(this.flushTimer);
    this.flushTimer = setTimeout(() => this.flushPending(), ON_CHANGE_DEBOUNCE_TIMER);
  }

  showQuantity(row, quantity) {
    const input = row.querySelector('.quantity__input');
    input.value = quantity;
    input.closest('quantity-input')?.validateQtyRules();

    row.dataset.quantity = quantity;
    row.style.display = quantity === 0 ? 'none' : '';
    row.classList.add('cart-item--updating');
    row.querySelectorAll('.cart-item__totals .price--end').forEach((price) => {
      const unitPrice = price.classList.contains('cart-item__old-price')
        ? row.dataset.originalPrice
        : row.dataset.finalPrice;
      this.setMoney(price, unitPrice * quantity);
    });
  }

  // An estimate until Shopify answers: a quantity break can still lower the unit price.
  showTotals() {
    const rows = [...this.querySelectorAll('[data-cart-item-key]')];
    const total = this.getFooterScope()?.querySelector('.totals__total-value');
    if (total?.dataset.cartTotal) {
      const difference = rows.reduce(
        (sum, row) => sum + row.dataset.finalPrice * row.dataset.quantity - row.dataset.lineTotal,
        0
      );
      this.setMoney(total, Number(total.dataset.cartTotal) + difference);
    }

    const itemCount = rows.reduce((sum, row) => sum + Number(row.dataset.quantity), 0);
    document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"]').forEach((bubble) => {
      bubble.textContent = itemCount;
    });
  }

  // Swaps only the number in the rendered text, which keeps the shop's money format ("1.234,56 €").
  setMoney(element, cents) {
    element.textContent = element.textContent.replace(/\d(?:[\d.,\s]*\d)?/, (rendered) => {
      const decimalSeparator = /([.,])\d{2}$/.exec(rendered)?.[1] ?? ',';
      const thousandsSeparator = decimalSeparator === ',' ? '.' : ',';
      const [whole, fraction] = (cents / 100).toFixed(2).split('.');
      return `${whole.replace(/\B(?=(\d{3})+$)/g, thousandsSeparator)}${decimalSeparator}${fraction}`;
    });
  }

  getFooterScope() {
    return this.closest('.drawer__inner') || document.getElementById('main-cart-footer');
  }

  // Checkout waits for Shopify, so it never starts with a quantity that has not arrived yet.
  setUpdating(isUpdating) {
    if (isUpdating) {
      this.updatingScope = this.updatingScope || this.getFooterScope();
      this.updatingScope?.classList.add('cart--updating');
      this.blockedCheckoutButton = this.updatingScope?.querySelector('.cart__checkout-button');
      if (this.blockedCheckoutButton) this.blockedCheckoutButton.disabled = true;
      this.lineItemStatusElement.setAttribute('aria-hidden', false);
      return;
    }

    this.updatingScope?.classList.remove('cart--updating');
    this.updatingScope = null;
    // A re-render brings its own button with the right state, only a kept one needs releasing.
    if (this.blockedCheckoutButton?.isConnected) this.blockedCheckoutButton.disabled = false;
    this.blockedCheckoutButton = null;
  }

  getRequestBody(content) {
    return JSON.stringify({
      ...content,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });
  }

  flushPending() {
    clearTimeout(this.flushTimer);
    this.flushTimer = null;
    if (this.requestRunning || !this.pendingRows.size) return;

    this.getErrorsElement().textContent = '';
    const sentRows = new Map(this.pendingRows);
    this.pendingRows.clear();
    this.requestRunning = true;

    const updates = [...sentRows].map(([row, quantity]) => [row.dataset.cartItemKey, quantity]);
    // One line stays on /cart/change, the route the fee and other cart apps listen to.
    const isSingleLine = updates.length === 1;
    const body = this.getRequestBody(
      isSingleLine ? { id: updates[0][0], quantity: updates[0][1] } : { updates: Object.fromEntries(updates) }
    );

    fetch(isSingleLine ? routes.cart_change_url : routes.cart_update_url, {
      ...fetchConfig(),
      keepalive: true,
      body,
    })
      .then((response) => response.text())
      .then((state) => {
        const parsedState = JSON.parse(state);
        this.requestRunning = false;

        const error = parsedState.errors || (parsedState.status ? parsedState.description || parsedState.message : null);
        if (error) {
          this.recover(error);
          return;
        }

        this.syncRowKeys(parsedState.items);

        // Newer quantities are waiting: rendering this answer would take them off the screen.
        if (this.pendingRows.size) {
          if (!this.flushTimer) this.flushPending();
          return;
        }

        this.renderCart(parsedState, sentRows);
      })
      .catch(() => {
        this.requestRunning = false;
        this.recover(window.cartStrings.error);
      });
  }

  // Shopify changes a line's key when its discount changes (e.g. a quantity break), so the keys of
  // the rows still on screen are refreshed from every answer.
  syncRowKeys(items) {
    const remainingItems = [...items];
    this.querySelectorAll('[data-cart-item-key]').forEach((row) => {
      if (Number(row.dataset.quantity) === 0 && !this.pendingRows.has(row)) return;

      const index = remainingItems.findIndex(
        (item) => `${item.variant_id}|${item.properties?._ID ?? ''}` === row.dataset.lineIdentity
      );
      if (index === -1) return;
      row.dataset.cartItemKey = remainingItems.splice(index, 1)[0].key;
    });
  }

  renderCart(parsedState, sentRows) {
    const { line, name, variantId, event, eventTarget } = this.lastChange;

    const rejected = [...sentRows].reduce((result, [row, quantity]) => {
      const index = parsedState.items.findIndex((item) => item.key === row.dataset.cartItemKey);
      if (quantity > 0 && index !== -1 && parsedState.items[index].quantity !== quantity) {
        return { line: index + 1, quantity: parsedState.items[index].quantity };
      }
      return result;
    }, null);

    CartPerformance.measure(`${eventTarget}:paint-updated-sections"`, () => {
      this.classList.toggle('is-empty', parsedState.item_count === 0);
      const cartDrawerWrapper = document.querySelector('cart-drawer');
      const cartFooter = document.getElementById('main-cart-footer');

      if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
      if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

      this.getSectionsToRender().forEach((section) => {
        const elementToReplace =
          document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
        elementToReplace.innerHTML = this.getSectionInnerHTML(
          parsedState.sections[section.section],
          section.selector
        );
      });

      this.updateLiveRegions(
        rejected ? rejected.line : line,
        rejected ? window.cartStrings.quantityError.replace('[quantity]', rejected.quantity) : ''
      );

      const lineItem =
        document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
      if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
        cartDrawerWrapper
          ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
          : lineItem.querySelector(`[name="${name}"]`).focus();
      } else if (parsedState.item_count === 0 && cartDrawerWrapper?.querySelector('.drawer__inner-empty')) {
        trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
      } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
        trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
      }
    });

    CartPerformance.measureFromEvent(`${eventTarget}:user-action`, event);

    publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
    this.setUpdating(false);
  }

  // Shopify refused or could not be reached: show what the cart really holds, with the reason.
  recover(message) {
    this.pendingRows.clear();
    clearTimeout(this.flushTimer);
    this.flushTimer = null;
    this.requestRunning = true;

    fetch(routes.cart_update_url, { ...fetchConfig(), body: this.getRequestBody({ updates: {} }) })
      .then((response) => response.json())
      .then((parsedState) => {
        this.requestRunning = false;
        this.syncRowKeys(parsedState.items);
        this.renderCart(parsedState, new Map());
        this.updateLiveRegions(this.lastChange.line, message);
      })
      .catch(() => {
        this.requestRunning = false;
        this.querySelectorAll('[data-cart-item-key]').forEach((row) => {
          this.showQuantity(row, Number(row.querySelector('.quantity__input').getAttribute('value')));
          row.classList.remove('cart-item--updating');
        });
        this.showTotals();
        this.setUpdating(false);
        this.getErrorsElement().textContent = window.cartStrings.error;
      });
  }

  getErrorsElement() {
    return document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
              .then(() => CartPerformance.measureFromEvent('note-update:user-action', event));
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
